import { Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import Decimal from 'decimal.js';
import { useCallback, useMemo, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Card } from '@/components/layout/Card/Card';
import { TableHeaderBar } from '@/components/tables/components/TableHeaderBar/TableHeaderBar';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { InsurancePolicies_InsurancePolicyFragment } from '@/modules/entities/InsurancePolicyDetailsSubform/graphql/InsurancePolicies.generated';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EditPolicySideDrawer } from './EditPolicySideDrawer';
import {
  ManuallyTrackedPoliciesTableRowData,
  mapPoliciesToRows,
  useColumns as useBaseColumns,
} from './HoldingsGroupPolicies.utils';

const MAX_ROW_COUNT = 10;

export interface HoldingsGroupPoliciesProps {
  policies: InsurancePolicies_InsurancePolicyFragment[];
  entity: EstatePage_EntityFragment;
}

export function HoldingsGroupPolicies({
  policies,
  entity,
}: HoldingsGroupPoliciesProps) {
  const canEdit = useCanEditHouseholdData();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] =
    useState<InsurancePolicies_InsurancePolicyFragment | null>(null);

  const handleEditClick = useCallback(
    (policyId: string) => {
      const policy = policies.find((p) => p.id === policyId);
      if (policy) {
        setEditingPolicy(policy);
        setEditDrawerOpen(true);
      }
    },
    [policies]
  );

  const handleDrawerClose = useCallback(() => {
    setEditDrawerOpen(false);
    setEditingPolicy(null);
  }, []);

  const totalDeathBenefit = useMemo(() => {
    return policies
      .filter((policy) => policy.deathBenefitAmount != null)
      .reduce(
        (sum, policy) => sum.add(policy.deathBenefitAmount!),
        new Decimal(0)
      );
  }, [policies]);

  // Get base columns
  const baseColumns = useBaseColumns();

  // Add edit button column
  const columnsWithEdit =
    useMemo((): GridColDef<ManuallyTrackedPoliciesTableRowData>[] => {
      if (!canEdit) {
        return baseColumns;
      }

      const editColumn: GridColDef<ManuallyTrackedPoliciesTableRowData> = {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <CellContainer align="center">
            <IconButton
              ariaLabel="Edit insurance policy"
              size="xs"
              variant="transparent"
              icon={Edit02Icon}
              onClick={() => handleEditClick(row.id)}
              data-testid="edit-policy-button"
            />
          </CellContainer>
        ),
      };

      return [...baseColumns, editColumn];
    }, [baseColumns, canEdit, handleEditClick]);

  const rows = useMemo(() => mapPoliciesToRows(policies), [policies]);

  if (policies.length === 0) {
    return null;
  }

  return (
    <>
      <Card variant="outlined">
        <Stack
          sx={{
            p: 2,
            borderRadius: '4px',
          }}
        >
          <Stack gap={2}>
            <Stack>
              <Typography variant="title-s">Policies</Typography>
              <Typography variant="body-xs">
                {formatCurrencyNoDecimals(totalDeathBenefit)} total death
                benefit
              </Typography>
            </Stack>
            <Stack>
              <TableHeaderBar header={'Manually tracked policies'} />
              <DataTable
                hideFooterPagination={rows.length <= MAX_ROW_COUNT}
                pageSize={MAX_ROW_COUNT}
                pagination={true}
                rows={rows}
                columns={columnsWithEdit}
                getRowHeight={() => 60}
                paginationMode="client"
                noPointer
                rowHoverVariant="transparent"
              />
            </Stack>
          </Stack>
        </Stack>
      </Card>
      {editingPolicy && (
        <EditPolicySideDrawer
          key={editingPolicy.id}
          isOpen={editDrawerOpen}
          onClose={handleDrawerClose}
          entity={entity}
          policy={editingPolicy}
        />
      )}
    </>
  );
}
